import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PlanCard from "@/components/PlanCard";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import { useI18n } from "@/lib/i18n";
import { Filter, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";

const FACINGS = ["", "N", "S", "E", "W", "NE", "NW", "SE", "SW"];

export default function Plans() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchInfo, setMatchInfo] = useState(null);

  const filter = {
    facing: params.get("facing") || "",
    floors: params.get("floors") || "",
    bhk: params.get("bhk") || "",
    state: params.get("state") || "",
    category: params.get("category") || "",
    min_size: params.get("min_size") || "",
    max_size: params.get("max_size") || "",
    length: params.get("length") || "",
    width: params.get("width") || "",
    naksha_no: params.get("naksha_no") || "",
  };

  const isMatchSearch = filter.length && filter.width && filter.floors && filter.facing;

  useEffect(() => {
    setLoading(true);
    if (isMatchSearch) {
      api.post("/plans/match", {
        length: parseFloat(filter.length), width: parseFloat(filter.width),
        floors: parseInt(filter.floors), facing: filter.facing,
      }).then(({ data }) => {
        setPlans(data);
        setMatchInfo({ count: data.length });
      }).finally(() => setLoading(false));
    } else {
      const q = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => {
        if (["length", "width"].includes(k)) return;
        const val = k === "naksha_no" ? v.trim() : v;
        if (!val) return;
        q.set(k, val);
      });
      api.get(`/plans?${q.toString()}`).then(({ data }) => {
        setPlans(data);
        setMatchInfo(null);
      }).finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const setF = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  return (
    <div data-testid="plans-page">
      <SEO
        title="Browse House Plans"
        description="Filter house plans by facing, floors, BHK and plot size. Instant BOQ and cost estimate with every plan."
        path="/plans"
      />
      <div className="bg-gradient-to-b from-emerald-50/40 to-white border-b border-slate-200">
        <div className="nc-container py-10 md:py-14">
          <h1 className="text-3xl md:text-4xl font-semibold text-slate-900">{t("plansPage.title")}</h1>
          <p className="text-slate-600 mt-2">{t("plansPage.sub", { n: plans.length })}</p>
        </div>
      </div>

      <div className="nc-container py-8">
        <div className="flex flex-wrap gap-2 mb-6 items-center" data-testid="plans-filters">
          <Filter className="w-4 h-4 text-slate-400" />
          <select className="nc-input max-w-[160px] py-2" value={filter.facing} onChange={(e) => setF("facing", e.target.value)} data-testid="filter-facing">
            {/* The blank option already means "any facing" and sends no filter.
                A second ANY option would read as a duplicate to customers and do
                exactly the same thing. ANY stays a plan-side tag only: a plan
                tagged ANY surfaces in every specific-facing search. */}
            <option value="">{t("plansPage.anyFacing")}</option>
            {FACINGS.filter(Boolean).map(f => <option key={f} value={f}>{t(`facing.${f}`)}</option>)}
          </select>
          <select className="nc-input max-w-[160px] py-2" value={filter.floors} onChange={(e) => setF("floors", e.target.value)} data-testid="filter-floors">
            <option value="">{t("plansPage.anyFloors")}</option>
            {[1, 2, 3, 4].map(n => <option key={n} value={n}>{t("plansPage.floorsOpt", { n })}</option>)}
          </select>
          <select className="nc-input max-w-[160px] py-2" value={filter.bhk} onChange={(e) => setF("bhk", e.target.value)} data-testid="filter-bhk">
            <option value="">{t("plansPage.anyBhk")}</option>
            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{t("plansPage.bhkOpt", { n })}</option>)}
          </select>
          <select className="nc-input max-w-[180px] py-2" value={filter.category} onChange={(e) => setF("category", e.target.value)} data-testid="filter-category">
            <option value="">{t("plansPage.allCategories")}</option>
            <option value="budget">{t("plansPage.catBudget")}</option>
            <option value="duplex">{t("plansPage.catDuplex")}</option>
            <option value="luxury">{t("plansPage.catLuxury")}</option>
            <option value="vastu">{t("plansPage.catVastu")}</option>
          </select>
          <input
            className="nc-input max-w-[220px] py-2"
            placeholder="Search by Naksha No (e.g. NK-1001)"
            value={filter.naksha_no}
            onChange={(e) => setF("naksha_no", e.target.value)}
            data-testid="filter-naksha-no"
          />
          {(filter.facing || filter.floors || filter.bhk || filter.category || filter.state || filter.naksha_no) && (
            <button onClick={() => setParams(new URLSearchParams())} className="text-sm text-emerald-700 font-medium" data-testid="clear-filters-btn">{t("plansPage.clearAll")}</button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="animate-spin" /></div>
        ) : plans.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="plans-grid">
            {matchInfo && (
              <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-4 text-sm font-medium" data-testid="match-info-banner">
                {t("plansPage.matchInfo", { count: matchInfo.count, length: filter.length, width: filter.width, floors: filter.floors, facing: t(`facing.${filter.facing}`) || filter.facing })}
              </div>
            )}
            {plans.map(p => <PlanCard key={p.id} plan={p} />)}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="no-match-block">
            <h3 className="text-2xl font-semibold text-slate-900">{t("plansPage.noMatchTitle")}</h3>
            <p className="text-slate-600 mt-2 max-w-md mx-auto">{t("plansPage.noMatchSub")}</p>
            <div className="mt-6 max-w-2xl mx-auto text-left">
              <LeadCaptureForm
                source="no_match"
                plotDetails={isMatchSearch ? filter : null}
                prefill={{
                  length: filter.length,
                  width: filter.width,
                  facing: filter.facing,
                  northSide: params.get("north_side") || "",
                  southSide: params.get("south_side") || "",
                  eastSide:  params.get("east_side")  || "",
                  westSide:  params.get("west_side")  || "",
                }}
                dataTestidPrefix="no-match-lead"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
